import {reservationTicketEmail} from '../../utils/reservation-ticket';
﻿import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { format, startOfDay } from 'date-fns';
import {
  BookingFormGuard,
  BookingFormGuardsService,
} from '../../services/booking-form-guards.service';
import { GuestEditSettingsService } from '../../services/guest-edit-settings.service';
import {
  BookingFormSettings,
  BookingFormSettingsService,
} from '../../services/booking-form-settings.service';
import {
  BookingFormExperience,
  BookingFormExperiencesService,
} from '../../services/booking-form-experiences.service';

interface UiConfig {
  alertHeading: string;
  alertMessageTemplate: string;
  alertAdvice: string;
}

interface Session {
  id: number;
  user_id: number;
  name: string;
  start_time: string;
  end_time: string;
  hall_id: number;
}

interface SalonOption {
  salon_name: string;
}

@Component({
    selector: 'app-manuel-reservation',
    templateUrl: './manuel-reservation.component.html',
    styleUrls: ['./manuel-reservation.component.scss'],
    standalone: false
})
export class ManuelReservationComponent implements OnInit {
  private ticketLink:string|null=null;
  public isLoading = false;
  public errorText = ''; // â† new
  public companyName: string | null = null;
  /**
   * defaultplus tenants get a completely different booking form (service
   * catalog, duration, slot-aware) rendered by the child component; the
   * restaurant flow below stays untouched (V-company-branch).
   */
  public isDefaultPlus = false;
  // CoreUI compares the complete Date value against each calendar cell at
  // midnight. Passing the current time would therefore disable today.
  public today = startOfDay(new Date());
  public currentStep = 1;
  public readonly totalSteps = 3;
  public stepValidationMessage = '';

  userId!: number;
  salons: SalonOption[] = [];
  baseSalons: SalonOption[] = [];
  reservationNumber!: string;

  // control flags for alerts
  showErrorNotification = false;

  /**
   * Terminal state: the reservation was created. The form is replaced by the
   * success page and cannot be edited or re-submitted — the guest can only
   * start a fresh reservation, which reloads the page.
   */
  isCompleted = false;

  // uiConfig will be initialized in ngOnInit
  uiConfig!: UiConfig;

  form = {
    startDate: '',
    startTime: '',
    personCount: 1,
    name: '',
    emailAddress: '',
    phoneNumber: '',
    salonName: '',
    // Optional guest remarks: allergies, anniversary, seating wishes.
    notes: '',
  };

  timePeriods = [
    {
      label: 'MANUAL_RESERVATION.MORNING',
      value: 'morning',
      start: '06:00',
      end: '12:00',
    },
    {
      label: 'MANUAL_RESERVATION.AFTERNOON',
      value: 'afternoon',
      start: '12:00',
      end: '18:00',
    },
    {
      label: 'MANUAL_RESERVATION.EVENING',
      value: 'evening',
      start: '18:00',
      end: '23:00',
    },
  ];

  selectedTimePeriod = ''; // kullanıcının seçtiği zaman dilimi silinecek
  private sessionRequestToken = 0;

  availableTimes: string[] = [];
  sessions: Session[] = [];
  baseSessions: Session[] = [];
  selectedSessionId: number | null = null;

  // Information guards the guest must consent to before booking
  guards: BookingFormGuard[] = [];
  guardConsents: Record<number, boolean> = {};

  // Guest-edit-disable: when the tenant switched the guest edit page off,
  // the confirmation email must not carry an edit button. Fail-open default.
  guestEditEnabled = true;

  // Tenant's form settings: party-size cap + custom over-limit message.
  // Nulls (default) mean 'no limit' — a tenant without settings, or a failed
  // fetch, must never block the form; the backend gate is the enforcement.
  formSettings: BookingFormSettings = { max_people: null, max_people_message: null };

  // Experiences / set-menus the guest must pick from (capture-only v1). The
  // dropdown exists only when the tenant toggle is on AND rows exist; in
  // every other state the payload keeps the legacy set_menu 'default'.
  experiencesEnabled = false;
  experiencesAllowCustom = false;
  experiences: BookingFormExperience[] = [];
  selectedExperience = '';
  // Sentinel value for the "Other" dropdown option (V135) — never a real
  // experience name, never sent to the backend.
  readonly OTHER_EXPERIENCE = '__other__';
  customExperience = '';

  allClosures: any[] = [];
  fullyClosedDates: Date[] = [];
  closedSessionKeys: Set<string> = new Set();
  isSelectedDateFullyClosed = false;
  selectedDateHasClosures = false;

  /**
   * Recurring weekly closures ("Kapalı Günler"), keyed by weekday name — a
   * separate mechanism from the dated /closed-dates entries, so the form has
   * to honour both or it will offer days the restaurant never opens.
   */
  private closedWeekdays: Record<string, boolean> = {};
  private readonly weekdayKeys = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ];

  /**
   * Predicate for the date picker: false disables the day. Reassigned (not
   * mutated) once the config loads, so the calendar's pure pipe re-evaluates.
   */
  dateFilter: (date: Date) => boolean = () => true;

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private translate: TranslateService,
    private guardsService: BookingFormGuardsService,
    private guestEditSettings: GuestEditSettingsService,
    private settingsService: BookingFormSettingsService,
    private experiencesService: BookingFormExperiencesService
  ) {
    const supportedLanguages = ['en', 'tr', 'es', 'el', 'de'];
    this.translate.addLangs(supportedLanguages);
    this.translate.setDefaultLang('en');
    const browserLang = navigator.language.split('-')[0];
    this.translate.use(supportedLanguages.includes(browserLang) ? browserLang : 'en');
  }

  ngOnInit(): void {
    // initialize uiConfig after translate is ready

    this.uiConfig = {
      alertHeading: this.translate.instant(
        'MANUAL_RESERVATION.ALERT_SUCCESS_HEADING'
      ),
      alertMessageTemplate: this.translate.instant(
        'MANUAL_RESERVATION.ALERT_SUCCESS_MESSAGE'
      ),
      alertAdvice: this.translate.instant(
        'MANUAL_RESERVATION.ALERT_SUCCESS_ADVICE'
      ),
    };

    this.route.paramMap.subscribe((params) => {
      const companyNameParam = params.get('companyName');
      if (companyNameParam) {
        this.companyName = companyNameParam;
        this.fetchUserIdByCompanyName(companyNameParam);
      }
    });
  }

  fetchUserIdByCompanyName(companyName: string): void {
    const url = `${environment.baseUrl}/users/get-id-by-company-name`;
    this.http
      .get<any>(url, {
        params: { company_name: companyName },
      })
      .subscribe({
        next: (res) => {
          this.userId = res.user_id;
          this.isDefaultPlus =
            String(res.company_type || '').trim().toLowerCase() === 'defaultplus';
          if (this.isDefaultPlus) {
            // The dp child owns its own catalog/slot loading; the restaurant
            // salon/session/guard pipeline below does not apply.
            return;
          }
          this.loadSalons(() => this.refreshEffectiveOptions());
          this.loadSessions(() => this.refreshEffectiveOptions());
          this.loadClosures();
          this.loadClosedWeekdays();
          this.loadGuards();
          this.loadGuestEditSetting();
          this.loadFormSettings();
          this.loadExperiences();
        },
        error: (err) => {
          console.error('Kullanıcı bulunamadı veya hata oluştu:', err);
          this.errorText = this.translate.instant('MANUAL_RESERVATION.ERROR_USER_NOT_FOUND');
          this.showErrorNotification = true;
        },
      });
  }

  loadGuards(): void {
    // The page already picks the UI language from the browser (constructor);
    // the backend translates guard texts into it, or serves the original.
    const lang = this.translate.currentLang || 'en';
    this.guardsService.getPublicGuards(this.userId, lang).subscribe({
      next: (guards) => {
        this.guards = guards;
        this.guardConsents = {};
        console.log(`[Guards] Loaded ${guards.length} information guard(s)`);
      },
      error: (err) => {
        // Fail open: an unreachable guards endpoint must not block a public
        // booking form — the backend gate is the real enforcement.
        console.error('[Guards] Failed to load information guards:', err);
        this.guards = [];
        this.guardConsents = {};
      },
    });
  }

  loadExperiences(): void {
    // Keep original names as booking values and use translations only as labels.
    const lang = this.translate.currentLang || 'en';
    this.experiencesService.getPublicExperiences(this.userId, lang).subscribe({
      next: (state) => {
        this.experiencesEnabled = state.experiences_enabled;
        this.experiencesAllowCustom = state.experiences_allow_custom === true;
        this.experiences = [...state.data].sort(
          (a, b) => a.sort_order - b.sort_order || a.id - b.id
        );
        console.log(
          `[Experiences] enabled=${this.experiencesEnabled}, allowCustom=${this.experiencesAllowCustom}, ${this.experiences.length} row(s)`
        );
      },
      error: (err) => {
        // Fail open: an unreachable endpoint must not block a public booking
        // form — the backend 400 gate is the real enforcement.
        console.error('[Experiences] Failed to load experiences:', err);
        this.experiencesEnabled = false;
        this.experiencesAllowCustom = false;
        this.experiences = [];
      },
    });
  }

  /** V130: the dropdown exists — and is required — only in this state. */
  get experienceSelectionActive(): boolean {
    return this.experiencesEnabled && this.experiences.length > 0;
  }

  /** True when the required experience pick is still missing. */
  get experienceMissing(): boolean {
    if (!this.experienceSelectionActive) {
      return false;
    }
    const selected = String(this.selectedExperience || '').trim();
    if (!selected) {
      return true;
    }
    // "Other" needs the custom text filled in (V135).
    if (selected === this.OTHER_EXPERIENCE) {
      return !String(this.customExperience || '').trim();
    }
    return false;
  }

  /** The set_menu value the payload carries for the current selection. */
  private experiencePayloadValue(): string {
    if (!this.experienceSelectionActive) {
      return 'default';
    }
    if (this.selectedExperience === this.OTHER_EXPERIENCE) {
      return String(this.customExperience || '').trim();
    }
    return this.selectedExperience;
  }

  /** Exactly one salon: auto-selected in loadSalons, picker hidden (V132). */
  get isSingleSalon(): boolean {
    return this.baseSalons.length === 1;
  }

  loadGuestEditSetting(): void {
    this.guestEditSettings.getPublicSettings(this.userId).subscribe({
      next: (settings) => {
        this.guestEditEnabled = settings?.edit_enabled !== false;
      },
      error: () => {
        // Fail open: an unreachable settings endpoint must not strip the
        // edit button for tenants that never disabled guest editing.
        this.guestEditEnabled = true;
      },
    });
  }

  loadFormSettings(): void {
    this.settingsService.getPublicSettings(this.userId).subscribe({
      next: (settings) => {
        this.formSettings = settings || { max_people: null, max_people_message: null };
        console.log(`[Settings] Party-size limit: ${this.formSettings.max_people ?? 'none'}`);
      },
      error: (err) => {
        // Fail open: an unreachable settings endpoint must not block a public
        // booking form — the backend gate is the real enforcement.
        console.error('[Settings] Failed to load form settings:', err);
        this.formSettings = { max_people: null, max_people_message: null };
      },
    });
  }

  /** True when the tenant set a party-size cap and the guest is over it. */
  get partySizeExceeded(): boolean {
    const limit = Number(this.formSettings?.max_people);
    if (!Number.isFinite(limit) || limit < 1) {
      return false;
    }
    return Number(this.form.personCount) > limit;
  }

  /** Tenant's custom over-limit text, or the translated default. */
  partySizeLimitMessage(): string {
    const custom = String(this.formSettings?.max_people_message || '').trim();
    if (custom) {
      return custom;
    }
    return this.translate.instant('MANUAL_RESERVATION.MAX_PEOPLE_DEFAULT', {
      max: this.formSettings?.max_people,
    });
  }

  /** Step-1 message: the party-size limit gets its own text, not the generic one. */
  private stepOneValidationMessage(): string {
    if (this.partySizeExceeded) {
      return this.partySizeLimitMessage();
    }
    if (this.experienceMissing) {
      return this.translate.instant('MANUAL_RESERVATION.VALIDATION_EXPERIENCE');
    }
    return this.translate.instant('MANUAL_RESERVATION.VALIDATION_STEP_ONE');
  }

  toggleGuardConsent(guard: BookingFormGuard): void {
    this.guardConsents[guard.id] = !this.guardConsents[guard.id];
    this.stepValidationMessage = '';
  }

  allGuardsAccepted(): boolean {
    return this.guards.every((g) => this.guardConsents[g.id] === true);
  }

  loadSalons(onDone?: () => void): void {
    const url = `${environment.baseUrl}/salon/get/all`;
    this.http.get<any>(url, { params: { user_id: this.userId } }).subscribe({
      next: (data) => {
        this.baseSalons = this.normalizeSalonRows(data);
        this.salons = [...this.baseSalons];
        if (
          this.form.salonName &&
          !this.salons.some((s) => s.salon_name === this.form.salonName)
        ) {
          this.form.salonName = '';
        }
        // Single-salon tenant: pick it for the guest so the picker can be
        // hidden while the payload still carries salon_name (V132).
        if (this.isSingleSalon && !this.form.salonName) {
          this.form.salonName = this.baseSalons[0].salon_name;
        }
        // Closures may have loaded before the salons did, and "every salon is
        // closed" cannot be decided without them.
        this.recomputeFullyClosedDates();
        this.refreshClosureState();
        onDone?.();
      },
      error: (err) => {
        console.error(err);
        onDone?.();
      },
    });
  }

  loadSessions(onDone?: () => void): void {
    const url = `${environment.baseUrl}/salon/sessions?user_id=${this.userId}`;
    this.http.get<any>(url).subscribe({
      next: (data) => {
        this.baseSessions = this.normalizeSessionRows(data);
        this.sessions = [...this.baseSessions];
        this.ensureSelectedSessionValidity();
        onDone?.();
      },
      error: (err) => {
        console.error('Session çekilemedi:', err);
        onDone?.();
      },
    });
  }

  private closureDateToKey(raw: string): string {
    const s = String(raw || '').trim();
    // dd.MM.yyyy or dd/MM/yyyy → yyyy-MM-dd
    if (/^\d{2}[./]\d{2}[./]\d{4}$/.test(s)) {
      const [d, m, y] = s.split(/[./]/);
      return `${y}-${m}-${d}`;
    }
    // ISO timestamp or yyyy-MM-dd — just take the date portion
    return s.slice(0, 10);
  }

  /** Weekly closed weekdays, configured under Calendar → "Kapalı Günler". */
  loadClosedWeekdays(): void {
    const url = `${environment.baseUrl}/closed-days/get?user_id=${this.userId}`;
    this.http.get<any>(url).subscribe({
      next: (res) => {
        this.closedWeekdays = res?.data || {};
        // New function identity so the calendar recomputes its disabled cells.
        this.dateFilter = (date: Date) => !this.isWeeklyClosed(date);
        console.log(
          '[ClosedWeekdays] Closed weekdays:',
          this.weekdayKeys.filter((k) => this.closedWeekdays[k]),
        );
        this.clearSelectedDateIfWeeklyClosed();
        this.refreshClosureState();
      },
      // 404 simply means the tenant never configured weekly closures.
      error: () => {
        this.closedWeekdays = {};
      },
    });
  }

  private isWeeklyClosed(date: Date | null): boolean {
    if (!date) return false;
    return !!this.closedWeekdays[this.weekdayKeys[date.getDay()]];
  }

  /** Drop a preselected date that turns out to fall on a closed weekday. */
  private clearSelectedDateIfWeeklyClosed(): void {
    const selected = this.form.startDate ? new Date(this.form.startDate) : null;
    if (selected && !isNaN(selected.getTime()) && this.isWeeklyClosed(selected)) {
      this.form.startDate = '';
      this.selectedSessionId = null;
      this.form.startTime = '';
      this.availableTimes = [];
    }
  }

  loadClosures(): void {
    const url = `${environment.baseUrl}/closed-dates/get/${this.userId}`;
    console.log('[Closures] Loading from', url);
    this.http.get<any>(url).subscribe({
      next: (res) => {
        console.log('[Closures] Raw API response:', res);
        this.allClosures = res?.data || [];
        console.log(`[Closures] Loaded ${this.allClosures.length} records`);
        if (this.allClosures.length > 0) {
          const uniqueDates = [...new Set(this.allClosures.map((e: any) => e.closed_date))];
          console.log('[Closures] Unique closed_date values in response:', uniqueDates);
          console.log('[Closures] Normalised to yyyy-MM-dd:', [...new Set(uniqueDates.map((d: any) => this.closureDateToKey(d)))]);
          console.log('[Closures] Sample record:', this.allClosures[0]);
        } else {
          console.warn('[Closures] allClosures is EMPTY — check if res.data is the right path. Full response was:', res);
        }
        this.recomputeFullyClosedDates();
        this.refreshClosureState();
      },
      error: (err) => {
        console.error('[Closures] Failed to load closure data', err);
      },
    });
  }

  /**
   * A day counts as fully closed when the tenant closed the whole day
   * (a closure row with neither salon nor session), or when every salon they
   * have is closed for that day — the day-level row is the tidy case, but
   * closing the salons one by one leaves the restaurant just as shut.
   */
  private isDayFullyClosed(dateClosures: any[]): boolean {
    if (dateClosures.some((e) => !e.salon_name && !e.session_name)) {
      return true;
    }

    const salonNames = this.baseSalons
      .map((s) => String(s.salon_name || '').trim().toLowerCase())
      .filter((name) => !!name);
    if (!salonNames.length) {
      return false;
    }

    const closedSalons = new Set(
      dateClosures
        .filter((e) => e.salon_name && !e.session_name)
        .map((e) => String(e.salon_name).trim().toLowerCase()),
    );
    return salonNames.every((name) => closedSalons.has(name));
  }

  /** Dates the calendar must not allow, recomputed whenever closures or salons load. */
  private recomputeFullyClosedDates(): void {
    const closuresByDate = new Map<string, any[]>();
    for (const closure of this.allClosures) {
      const key = this.closureDateToKey(closure.closed_date);
      if (!key) continue;
      const bucket = closuresByDate.get(key);
      bucket ? bucket.push(closure) : closuresByDate.set(key, [closure]);
    }

    const dates: Date[] = [];
    closuresByDate.forEach((rows, key) => {
      if (!this.isDayFullyClosed(rows)) return;
      const parts = key.split('-');
      if (parts.length !== 3) return;
      dates.push(new Date(+parts[0], +parts[1] - 1, +parts[2], 0, 0, 0, 0));
    });

    this.fullyClosedDates = dates;
    console.log(
      `[Closures] Fully-closed dates (${dates.length}):`,
      dates.map((d) => this.getDateKeyFromValue(d)),
    );
  }

  private refreshClosureState(): void {
    const dateKey = this.getDateKeyFromValue(this.form.startDate);
    console.log(`[ClosureState] Refreshing — date="${dateKey}" salon="${this.form.salonName}"`);
    if (!dateKey) {
      this.closedSessionKeys = new Set();
      this.isSelectedDateFullyClosed = false;
      this.selectedDateHasClosures = false;
      console.log('[ClosureState] No date selected — state cleared');
      return;
    }

    const dateClosures = this.allClosures.filter(
      (e) => this.closureDateToKey(e.closed_date) === dateKey,
    );
    console.log(`[ClosureState] ${dateClosures.length} closure record(s) for "${dateKey}"`, dateClosures);

    const weeklyClosed = this.isWeeklyClosed(
      this.form.startDate ? new Date(this.form.startDate) : null,
    );
    this.isSelectedDateFullyClosed =
      weeklyClosed || this.isDayFullyClosed(dateClosures);
    this.selectedDateHasClosures = dateClosures.length > 0;

    const salonName = (this.form.salonName || '').trim().toLowerCase();
    const partial = dateClosures.filter(
      (e) =>
        e.session_name &&
        (!salonName ||
          !e.salon_name ||
          String(e.salon_name).trim().toLowerCase() === salonName),
    );
    this.closedSessionKeys = new Set(
      partial.map((e) => String(e.session_name).trim().toLowerCase()),
    );
    console.log(`[ClosureState] fullyClosedDay=${this.isSelectedDateFullyClosed} | closedSessions:`, [...this.closedSessionKeys]);
  }

  isSessionClosed(session: Session): boolean {
    if (this.isSelectedDateFullyClosed) return true;
    return this.closedSessionKeys.has(String(session.name).trim().toLowerCase());
  }

  selectSession(session: Session): void {
    const closed = this.isSessionClosed(session);
    console.log(`[Session] Click — id=${session.id} name="${session.name}" closed=${closed}`);
    if (closed) return;
    this.selectedSessionId = session.id;
    this.stepValidationMessage = '';
    console.log(`[Session] Selected — id=${session.id} name="${session.name}" window=${session.start_time}–${session.end_time}`);
    this.onSessionChange();
  }

  selectStartTime(time: string): void {
    this.form.startTime = time;
    this.stepValidationMessage = '';
  }

  onSubmit(): void {
    if (!this.isStepOneValid()) {
      this.currentStep = 1;
      this.stepValidationMessage = this.stepOneValidationMessage();
      return;
    }
    if (!this.isStepTwoValid()) {
      this.currentStep = 2;
      this.stepValidationMessage = this.translate.instant('MANUAL_RESERVATION.VALIDATION_STEP_TWO');
      return;
    }
    if (!this.areContactFieldsValid()) {
      this.currentStep = 3;
      this.stepValidationMessage = this.translate.instant('MANUAL_RESERVATION.VALIDATION_STEP_THREE');
      return;
    }
    if (!this.allGuardsAccepted()) {
      this.currentStep = 3;
      this.stepValidationMessage = this.translate.instant('MANUAL_RESERVATION.VALIDATION_GUARDS');
      return;
    }

    this.stepValidationMessage = '';
    this.isLoading = true;
    this.showErrorNotification = false;

    const fetchIdUrl = `${environment.baseUrl}/generate-reservation-id?userId=${this.userId}`;
    this.http.get<{ reservationId: string }>(fetchIdUrl).subscribe(
      (res) => {
        this.reservationNumber = res.reservationId;
        this.createReservation();
      },
      (err) => {
        console.error(err);
        this.isLoading = false;
        this.showErrorNotification = true;
        this.errorText =
          err?.error?.message || err?.message || this.translate.instant('MANUAL_RESERVATION.ERROR_GENERATE_ID');
      }
    );
  }

  createReservation(): void {
    const date = this.getDateKeyFromValue(this.form.startDate);
    if (!date) {
      this.isLoading = false;
      this.showErrorNotification = true;
      this.errorText = this.translate.instant('MANUAL_RESERVATION.ERROR_INVALID_DATE');
      return;
    }
    const time = this.form.startTime + ':00';
    const trimmedPhone = this.normalizePhoneNumber(this.form.phoneNumber);

    const payload = {
      source: 'online booking form',
      source_id: this.form.emailAddress,
      pick_up: false,
      service: 'standard',
      // Capture-only v1: the picked experience name — or the guest's custom
      // "Other" text when the tenant opted in (V135) — goes into set_menu
      // verbatim; with the feature off the legacy placeholder stays so the
      // payload is byte-identical to before (V129/V130).
      set_menu: this.experiencePayloadValue(),
      date,
      time,
      num_people: this.form.personCount,
      name: this.form.name,
      email_address: this.form.emailAddress,
      customer_phone: trimmedPhone,
      salon_name: this.form.salonName,
      notes: this.form.notes.trim() || null,
      user_id: this.userId,
      id: this.reservationNumber,
      // ids of the information guards the guest ticked; the backend verifies
      // them against the tenant's enabled guards and snapshots the wording
      acknowledged_guard_ids: this.guards.map((g) => g.id),
    };

    this.http
      .post<any>(`${environment.baseUrl}/post/set_reservation`, payload)
      .subscribe(
        (res) => {
          this.ticketLink=res?.ticket_link||null;
          // The backend refuses a second active booking for the same guest and
          // slot, answering 200 with the booking they already hold and writing
          // no row. Two things follow. The id generated above belongs to
          // nothing, so showing it would hand the guest a code the restaurant
          // cannot look up; the existing booking is what gets rendered. And the
          // confirmation email must not go out again — it is composed here, in
          // the browser, so the server skipping its own side effects cannot
          // stop it, and a second "booked!" in the guest's inbox is exactly the
          // crossed message the guard exists to prevent.
          //
          // It stays a success either way. An error screen here is what starts
          // the retry that creates the next duplicate.
          if (res?.duplicate === true) {
            this.reservationNumber =
              res.existing_reservation_id ?? this.reservationNumber;
            this.form.startDate = res.date ?? this.form.startDate;
            this.form.startTime =
              String(res.time ?? '').slice(0, 5) || this.form.startTime;
          } else {
            this.sendReservationConfirmationEmail(payload);
          }
          this.isLoading = false;
          this.showErrorNotification = false;
          this.isCompleted = true;
        },
        (err) => {
          this.isLoading = false;
          this.showErrorNotification = true;

          this.errorText =
            (err.error && err.error.message) ||
            err.message ||
            this.translate.instant('MANUAL_RESERVATION.ERROR_UNKNOWN');
        }
      );
  }

  sendReservationConfirmationEmail(reservation: any): void {
    const dateObj = new Date(reservation.date + 'T00:00:00');
    const dateFormatted = this.getEmailDate(dateObj);
    const timeStr = reservation.time?.slice(0, 5);

    const companyName = this.companyName || 'Sagel';
    const reservationId = reservation.id;
    const encodedCompanyName = encodeURIComponent(companyName);
    const cancelLink = `${environment.baseUrl}/#/cancel/${encodedCompanyName}/${reservationId}`;
    const editLink = `${environment.baseUrl}/#/edit/${encodedCompanyName}/${reservationId}`;

    const t = (key: string, params?: object) =>
      this.translate.instant(`EMAIL_CONFIRMATION.${key}`, params);

    const bodyText = t('BODY_NEW', {
      numPeople: `<strong>${reservation.num_people}</strong>`,
      date: `<strong>${dateFormatted}</strong>`,
      time: `<strong>${timeStr}</strong>`,
      salon: `<strong>${reservation.salon_name}</strong>`,
    });

    // The notices the guest ticked on the form, restated in the email so they
    // keep a record of what they agreed to. Tenant-authored text, so escape it.
    const guardsBlock = this.guards.length
      ? `
              <table width="100%" cellspacing="0" cellpadding="0" style="margin-bottom:30px;">
                <tr>
                  <td style="padding:14px 18px;background:#f7f9ff;border:1px solid #dbe4ff;border-radius:14px;">
                    <div style="font-size:12px;letter-spacing:0.12em;text-transform:uppercase;color:#5f6a87;font-weight:600;margin-bottom:10px;">
                      ${t('GUARDS_TITLE')}
                    </div>
                    ${this.guards
                      .map(
                        (g) => `
                    <div style="font-size:13px;line-height:1.5;color:#1f2a44;margin-bottom:6px;">
                      &#10003; ${this.escapeHtml(g.text)}
                    </div>`,
                      )
                      .join('')}
                  </td>
                </tr>
              </table>`
      : '';

    const message = `
      <div style="font-family: 'Inter', Arial, sans-serif; background:#f4f6fb; padding:32px 16px;">
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:560px;margin:0 auto;background:#fff;border-radius:18px;overflow:hidden;box-shadow:0 18px 45px rgba(9,21,54,0.12);">
          <tr>
            <td style="background:linear-gradient(130deg,#01030d,#08153a);padding:28px 32px;text-align:center;color:#fff;">
              <div style="font-size:12px;letter-spacing:0.4em;text-transform:uppercase;opacity:0.7;">${t('HEADER_NEW')}</div>
              <h1 style="margin:12px 0 0;font-size:26px;">${companyName}</h1>
              <div style="margin-top:6px;font-size:13px;opacity:.8;">ID ${reservationId}</div>
            </td>
          </tr>
          <tr>
            <td style="padding:30px 32px;color:#1b2439;">
              <p style="margin:0 0 30px;font-size:15px;line-height:1.6;">${bodyText}</p>

              <table width="100%" cellspacing="0" cellpadding="0" style="margin-bottom:30px;">
                <tr>
                  <td style="padding:14px 18px;background:#f3f6ff;border-radius:14px;">
                    <table width="100%" cellspacing="0" cellpadding="0" style="font-size:14px;line-height:1.4;color:#1f2a44;">
                      <tr>
                        <td style="width:40%;">${t('DATE_LABEL')}</td>
                        <td><strong>${dateFormatted}</strong></td>
                      </tr>
                      <tr>
                        <td style="padding-top:8px;">${t('TIME_LABEL')}</td>
                        <td style="padding-top:8px;"><strong>${timeStr}</strong></td>
                      </tr>
                      <tr>
                        <td style="padding-top:8px;">${t('PARTY_LABEL')}</td>
                        <td style="padding-top:8px;"><strong>${reservation.num_people} ${t('GUESTS_SUFFIX')}</strong></td>
                      </tr>
                      <tr>
                        <td style="padding-top:8px;">${t('SALON_LABEL')}</td>
                        <td style="padding-top:8px;"><strong>${reservation.salon_name}</strong></td>
                      </tr>
                      ${
                        reservation.set_menu && reservation.set_menu !== 'default'
                          ? `<tr>
                        <td style="padding-top:8px;">${t('EXPERIENCE_LABEL')}</td>
                        <td style="padding-top:8px;"><strong>${this.escapeHtml(reservation.set_menu)}</strong></td>
                      </tr>`
                          : ''
                      }
                      ${
                        reservation.notes
                          ? `<tr>
                        <td style="padding-top:8px;vertical-align:top;">${t('NOTES_LABEL')}</td>
                        <td style="padding-top:8px;"><strong>${this.escapeHtml(reservation.notes)}</strong></td>
                      </tr>`
                          : ''
                      }
                    </table>
                  </td>
                </tr>
              </table>
${guardsBlock}
              <div style="text-align:center;margin:0 0 24px;">
                ${
                  this.guestEditEnabled
                    ? `<a href="${editLink}"
                  style="display:inline-block;min-width:160px;padding:11px 22px;border-radius:8px;background:#1a3d8f;border:none;color:#ffffff;text-decoration:none;font-weight:600;font-size:13px;line-height:1.4;margin:6px;">
                  ${t('EDIT_BUTTON')}
                </a>`
                    : ''
                }
                <a href="${cancelLink}"
                  style="display:inline-block;min-width:160px;padding:11px 22px;border-radius:8px;background:#c03535;border:none;color:#ffffff;text-decoration:none;font-weight:600;font-size:13px;line-height:1.4;margin:6px;">
                  ${t('CANCEL_BUTTON')}
                </a>
              </div>

              <p style="font-size:12px;color:#6c7794;text-align:center;">${t('HELP_TEXT_NEW')}</p>
            </td>
          </tr>
          <tr>
            <td style="background:#fafbff;padding:18px 24px;text-align:center;font-size:12px;color:#7f8aac;">
              <div style="letter-spacing:0.3em;text-transform:uppercase;">${t('POWERED_BY')}</div>
            </td>
          </tr>
        </table>
      </div>
    `;

    const emailPayload = {
      header: `Reservation Confirmation ${reservationId}`,
      content: reservationTicketEmail(message,this.ticketLink,this.translate.instant('RESERVATION_TICKET.VIEW_MANAGE')),
      to_addr: reservation.email_address,
    };

    this.http
      .post(`${environment.baseUrl}/email_notifications/info`, emailPayload)
      .subscribe({
        next: () => console.log('Confirmation email sent successfully.'),
        error: (err) => console.error('Failed to send confirmation email:', err),
      });
  }

  /** Guard texts are tenant-authored and go into an HTML email as markup. */
  private escapeHtml(value: string): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private getEmailDate(date: Date): string {
    const lang = this.translate.currentLang || 'en';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return (lang === 'tr' || lang === 'de') ? `${day}.${month}.${year}` : `${day}/${month}/${year}`;
  }

  /** Dismiss the error modal; the form stays editable so the guest can retry. */
  onCloseAlert(): void {
    this.showErrorNotification = false;
  }

  /**
   * Start over after a completed reservation. A reload is the only way to be
   * sure nothing from the previous booking (reservation id, ticked guards,
   * loaded sessions) leaks into the next one.
   */
  startNewReservation(): void {
    window.location.reload();
  }

  parseTemplate(template: string, data: any): string {
    return template.replace(/{{(\w+)}}/g, (match, key) => data[key] || '');
  }

  onSessionChange(): void {
    this.stepValidationMessage = '';
    const selected = this.sessions.find((s) => s.id === this.selectedSessionId);
    if (!selected) {
      this.availableTimes = [];
      this.form.startTime = '';
      return;
    }

    const [startHour, startMin] = selected.start_time.split(':').map(Number);
    const [endHour, endMin] = selected.end_time.split(':').map(Number);

    const startTotal = startHour * 60 + startMin;
    const endTotal = endHour * 60 + endMin;

    const step = 15;
    const result: string[] = [];
    const now = new Date();
    const isToday =
      this.getDateKeyFromValue(this.form.startDate) === format(now, 'yyyy-MM-dd');
    const currentMinute =
      now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60;

    let loopEnd = endTotal;
    if (endTotal < startTotal) {
      loopEnd = endTotal + 24 * 60;
    }

    for (let mins = startTotal; mins <= loopEnd; mins += step) {
      // On today's date, expose only slots that can still be booked. Values
      // above 24 hours belong to an overnight session and remain in the future.
      if (isToday && mins <= currentMinute) {
        continue;
      }
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      const normalizedHour = h % 24;
      result.push(
        `${normalizedHour.toString().padStart(2, '0')}:${m
          .toString()
          .padStart(2, '0')}`
      );
    }

    this.availableTimes = result;
    if (this.form.startTime && !this.availableTimes.includes(this.form.startTime)) {
      this.form.startTime = '';
    }
    console.log(`[Session] Time slots generated for "${selected.name}": ${this.availableTimes.length} slots (${this.availableTimes[0]} – ${this.availableTimes[this.availableTimes.length - 1]})`);
  }

  onStartDateChange(value: Date | string): void {
    const newKey = this.getDateKeyFromValue(value);
    if (newKey && newKey === this.getDateKeyFromValue(this.form.startDate)) {
      return; // c-date-picker fires on init — date hasn't actually changed
    }
    this.form.startDate = value as any;
    this.stepValidationMessage = '';
    console.log(`[Step1] Date changed → "${newKey}"`);
    this.refreshEffectiveOptions();
    this.refreshClosureState();
  }

  onSalonChange(salonName: string): void {
    this.form.salonName = salonName || '';
    this.stepValidationMessage = '';
    console.log(`[Step1] Salon changed → "${this.form.salonName}"`);
    this.refreshEffectiveSessions();
    this.refreshClosureState();
  }

  nextStep(): void {
    console.log(`[Nav] nextStep called from step ${this.currentStep}`);
    if (!this.canProceedFromCurrentStep()) {
      console.warn(`[Nav] Blocked — step ${this.currentStep} is not complete`);
      return;
    }
    if (this.currentStep < this.totalSteps) {
      this.currentStep += 1;
      if (this.currentStep === 2) {
        // Always re-sync closure state when entering the session step
        this.refreshClosureState();
        this.ensureSelectedSessionValidity();
      }
      console.log(`[Nav] Advanced to step ${this.currentStep}`);
    }
  }

  prevStep(): void {
    this.stepValidationMessage = '';
    if (this.currentStep > 1) {
      this.currentStep -= 1;
      console.log(`[Nav] Back to step ${this.currentStep}`);
    }
  }

  goToStep(step: number): void {
    if (step < 1 || step > this.totalSteps) {
      return;
    }
    if (step === this.currentStep) {
      return;
    }
    if (step < this.currentStep) {
      this.stepValidationMessage = '';
      this.currentStep = step;
      console.log(`[Nav] Jumped back to step ${step}`);
      return;
    }
    // Going forward — validate each preceding step
    if (step >= 2 && !this.isStepOneValid()) {
      this.stepValidationMessage = this.stepOneValidationMessage();
      console.warn('[Nav] Cannot advance — step 1 incomplete');
      return;
    }
    if (step >= 3 && !this.isStepTwoValid()) {
      this.stepValidationMessage = this.translate.instant('MANUAL_RESERVATION.VALIDATION_STEP_TWO');
      console.warn('[Nav] Cannot advance — step 2 incomplete');
      return;
    }
    this.stepValidationMessage = '';
    this.currentStep = step;
    if (step === 2) {
      // Always re-sync closure state when entering the session step
      this.refreshClosureState();
      this.ensureSelectedSessionValidity();
    }
    console.log(`[Nav] Jumped forward to step ${step}`);
  }

  canAccessStep(step: number): boolean {
    if (step <= 1) {
      return true;
    }
    if (step === 2) {
      return this.isStepOneValid();
    }
    if (step === 3) {
      return this.isStepOneValid() && this.isStepTwoValid();
    }
    return false;
  }

  private canProceedFromCurrentStep(): boolean {
    this.stepValidationMessage = '';
    if (this.currentStep === 1 && !this.isStepOneValid()) {
      this.stepValidationMessage = this.stepOneValidationMessage();
      return false;
    }
    if (this.currentStep === 2 && !this.isStepTwoValid()) {
      this.stepValidationMessage = this.translate.instant('MANUAL_RESERVATION.VALIDATION_STEP_TWO');
      return false;
    }
    return true;
  }

  isStepOneValid(): boolean {
    const hasDate = !!this.getDateKeyFromValue(this.form.startDate);
    const hasSalon = !!String(this.form.salonName || '').trim();
    const hasPeople = Number(this.form.personCount) >= 1;
    return (
      hasDate &&
      hasSalon &&
      hasPeople &&
      !this.partySizeExceeded &&
      !this.experienceMissing
    );
  }

  isStepTwoValid(): boolean {
    return this.selectedSessionId !== null && !!this.form.startTime;
  }

  isStepThreeValid(): boolean {
    return this.areContactFieldsValid() && this.allGuardsAccepted();
  }

  private areContactFieldsValid(): boolean {
    const hasName = !!String(this.form.name || '').trim();
    const email = String(this.form.emailAddress || '').trim();
    const phone = this.normalizePhoneNumber(this.form.phoneNumber);
    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const validPhone = /^[0-9+()\-\s]{7,20}$/.test(phone);
    return hasName && validEmail && validPhone;
  }

  trackBySalonName(_index: number, salon: { salon_name: string }): string {
    return salon.salon_name;
  }

  private normalizePhoneNumber(value: string): string {
    return String(value || '').trim();
  }

  private refreshEffectiveOptions(): void {
    this.refreshEffectiveSalons();
    this.refreshEffectiveSessions();
  }

  private refreshEffectiveSalons(): void {
    const dateKey = this.getDateKeyFromValue(this.form.startDate);
    if (!this.userId || !dateKey) {
      this.salons = [...this.baseSalons];
      return;
    }
    this.http
      .get<any>(`${environment.baseUrl}/salon/effective`, {
        params: {
          user_id: String(this.userId),
          reservation_date: dateKey,
        },
      })
      .subscribe({
        next: (res) => {
          const rows = this.normalizeSalonRows(res);
          this.salons = rows;
          if (
            this.form.salonName &&
            !this.salons.some((s) => s.salon_name === this.form.salonName)
          ) {
            this.form.salonName = '';
            this.refreshEffectiveSessions();
          }
          // Hidden picker for a single-salon tenant: re-apply the auto-pick
          // when the salon is open on the new date (V132).
          if (
            this.isSingleSalon &&
            !this.form.salonName &&
            this.salons.length === 1
          ) {
            this.form.salonName = this.salons[0].salon_name;
          }
        },
        error: () => {
          this.salons = [...this.baseSalons];
        },
      });
  }

  private refreshEffectiveSessions(): void {
    const token = ++this.sessionRequestToken;
    const dateKey = this.getDateKeyFromValue(this.form.startDate);
    if (!this.userId || !dateKey) {
      this.sessions = [...this.baseSessions];
      this.refreshClosureState();
      this.ensureSelectedSessionValidity();
      return;
    }

    this.http
      .get<any>(`${environment.baseUrl}/salon/sessions/effective`, {
        params: {
          user_id: String(this.userId),
          reservation_date: dateKey,
          ...(this.form.salonName ? { salon_name: this.form.salonName } : {}),
        },
      })
      .subscribe({
        next: (res) => {
          if (token !== this.sessionRequestToken) return;
          this.sessions = this.normalizeSessionRows(res);
          this.refreshClosureState();
          this.ensureSelectedSessionValidity();
        },
        error: () => {
          if (token !== this.sessionRequestToken) return;
          this.sessions = [...this.baseSessions];
          this.refreshClosureState();
          this.ensureSelectedSessionValidity();
        },
      });
  }


  private ensureSelectedSessionValidity(): void {
    if (this.selectedSessionId) {
      const found = this.sessions.find((s) => s.id === this.selectedSessionId);
      if (!found || this.isSessionClosed(found)) {
        // Session no longer exists or is now closed — clear the selection
        this.selectedSessionId = null;
        this.form.startTime = '';
        this.availableTimes = [];
        return;
      }
      this.onSessionChange();
    }
  }

  private normalizeSessionRows(response: any): Session[] {
    const rows = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response?.sessions)
        ? response.sessions
        : Array.isArray(response)
          ? response
          : [];

    return rows
      .map((row: any) => ({
        id: Number(row?.id),
        user_id: Number(row?.user_id || this.userId || 0),
        name: String(row?.name || '').trim(),
        start_time: String(row?.start_time || '').trim(),
        end_time: String(row?.end_time || '').trim(),
        hall_id: Number(row?.hall_id || 0),
      }))
      .filter(
        (row: Session) =>
          Number.isFinite(row.id) &&
          !!row.name &&
          !!row.start_time &&
          !!row.end_time
      );
  }

  private normalizeSalonRows(response: any): SalonOption[] {
    const rows = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response?.salons)
        ? response.salons
        : Array.isArray(response)
          ? response
          : [];

    const names = rows
      .map((row: any) =>
        typeof row === 'string'
          ? row.trim()
          : String(row?.salon_name || '').trim()
      )
      .filter((name: string) => !!name);

    const unique: Record<string, string> = {};
    names.forEach((name: string) => {
      const key = name.toLowerCase();
      if (!unique[key]) {
        unique[key] = name;
      }
    });
    return Object.values(unique)
      .sort((a, b) => a.localeCompare(b))
      .map((salon_name) => ({ salon_name }));
  }

  private getDateKeyFromValue(value: Date | string | null | undefined): string | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      if (isNaN(value.getTime())) {
        return null;
      }
      return format(value, 'yyyy-MM-dd');
    }

    const raw = String(value).trim();
    if (!raw) {
      return null;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      return raw;
    }
    if (/^\d{2}[./]\d{2}[./]\d{4}$/.test(raw)) {
      const [day, month, year] = raw.split(/[./]/);
      return `${year}-${month}-${day}`;
    }
    const parsed = new Date(raw);
    if (isNaN(parsed.getTime())) {
      return null;
    }
    return format(parsed, 'yyyy-MM-dd');
  }

  compareById(a: any, b: any): boolean {
    return a === b;
  }
}
